// Platform detection helpers. Centralised so the iOS gating logic for
// Google sign-in stays consistent across every entry point.
//
// Google sign-in is hidden in two cases:
//   • Native iOS App Store build (Capacitor) — App Store Guideline 4.
//     Apps must not jump out to Safari for auth, so we ship Apple + email.
//   • Mobile Safari on iPhone / iPad — Emergent Auth's OAuth callback is
//     unreliable inside mobile Safari due to Intelligent Tracking
//     Prevention (third-party cookies blocked, request killed when tab
//     backgrounds during the redirect). Users hit a "won't load" state
//     mid-callback. Apple Sign-In and email/password work fine, so we
//     hide the broken option and steer folks to the working ones.
//
// Desktop web (Chrome/Safari/Firefox/Edge) and Android still get Google.

import { Capacitor } from "@capacitor/core";

export const isNativeIOS = () =>
  typeof Capacitor !== "undefined" && Capacitor.getPlatform() === "ios";

// True when we're running inside mobile Safari on an iPhone or iPad
// (i.e., NOT the App Store Capacitor build — that's caught by isNativeIOS
// above). Detects via the UA string, which is stable across iOS versions
// for both iPhone and iPad "Request Desktop Site" edge case.
const isIOSMobileWeb = () => {
  if (typeof window === "undefined" || isNativeIOS()) return false;
  const ua = window.navigator?.userAgent || "";
  // iPadOS 13+ ships a Mac-like UA by default. Detect the iPad-in-Safari
  // case via `ontouchend` on the document — iPads always support touch,
  // Macs never do (even MacBooks with touch bars).
  const isIPadOS13Plus =
    /Macintosh/i.test(ua) && typeof document !== "undefined" && "ontouchend" in document;
  return /iPhone|iPad|iPod/i.test(ua) || isIPadOS13Plus;
};

// Web + Android web still get Google. iOS Capacitor + iPhone/iPad Safari
// hide it so users don't hit the broken OAuth callback.
export const showGoogleSignIn = () => !isNativeIOS() && !isIOSMobileWeb();
